import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, UploadCloud, Eraser, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { DebouncedInput } from "@/components/DebouncedField";
import { toast } from "sonner";

const BG_KEYS = ["default", "bedding", "curtain", "tableLinen", "placemat", "throw"];
const LOGO_KEYS = ["main", "white", "black"];

async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.url;
}

async function uploadLogoFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/upload-logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.url;
}

async function removeBg(url) {
  const { data } = await api.post("/utils/remove-bg", { url });
  return data.url;
}

export default function BrandDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const [brand, setBrand] = useState(null);
  const [removingBg, setRemovingBg] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/brands/${id}`);
    setBrand(data);
  };

  useEffect(() => { load(); }, [id]);

  const save = async (patch) => {
    const merged = { ...brand, ...patch };
    setBrand(merged);
    try {
      await api.put(`/brands/${id}`, merged);
    } catch (e) {
      toast.error(t("brandDetail.saveFailed"));
    }
  };

  const handleLogoUpload = async (key, file) => {
    if (!file) return;
    try {
      const url = await uploadLogoFile(file);
      save({ logos: { ...brand.logos, [key]: url } });
      toast.success(t("brandDetail.logoUpdated"));
    } catch (e) { toast.error(t("brandDetail.uploadFailed")); }
  };

  const handleRemoveBg = async (key) => {
    const url = brand.logos?.[key];
    if (!url) return;
    setRemovingBg(key);
    try {
      const newUrl = await removeBg(url);
      save({ logos: { ...brand.logos, [key]: newUrl } });
      toast.success(t("brandDetail.whiteBgRemoved"));
    } catch (e) { toast.error(t("brandDetail.removeBgFailed")); } finally { setRemovingBg(null); }
  };

  const handleBgUpload = async (key, file) => {
    if (!file) return;
    try {
      const url = await uploadFile(file);
      save({ backgrounds: { ...brand.backgrounds, [key]: url } });
      toast.success(t("brandDetail.bgUpdated"));
    } catch (e) { toast.error(t("brandDetail.uploadFailed")); }
  };

  if (!brand) return <div className="p-10 text-sm text-slate-400">{t("common.loading")}</div>;

  return (
    <div className="mx-auto max-w-4xl px-8 py-10" data-testid="brand-detail-page">
      <button onClick={() => navigate("/brands")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800" data-testid="back-to-brands-button">
        <ArrowLeft className="h-4 w-4" /> {t("brandDetail.back")}
      </button>
      {!isAdmin && (
        <div className="mt-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-700" data-testid="brand-readonly-banner">
          <Lock className="h-3.5 w-3.5" /> {t("common.readOnly")} · {t("common.adminOnly")}
        </div>
      )}
      <fieldset disabled={!isAdmin} className="m-0 min-w-0 border-0 p-0">
      <div className="mt-4 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-700 text-slate-900">{brand.brandName}</h1>
          <p className="font-mono-bhf text-xs text-slate-400">{brand.brandCode}</p>
        </div>
        <Select value={brand.status} onValueChange={(v) => save({ status: v })}>
          <SelectTrigger className="w-32" data-testid="brand-status-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="Active">{t("products.active")}</SelectItem><SelectItem value="Inactive">{t("products.inactive")}</SelectItem></SelectContent>
        </Select>
      </div>

      <div className="mt-8 bhf-card p-6">
        <h2 className="font-display text-sm font-700 text-slate-800">{t("brandDetail.logos")}</h2>
        <div className="mt-4 grid grid-cols-3 gap-4">
          {LOGO_KEYS.map((key) => (
            <div key={key}>
              <div className="flex h-24 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 p-2">
                {brand.logos?.[key] ? <img src={fileUrl(brand.logos[key])} alt={key} className="h-full object-contain" /> : <span className="text-xs text-slate-400">{t("brandDetail.noLogo")}</span>}
              </div>
              <label className="mt-2 flex cursor-pointer items-center justify-center gap-1.5 rounded-md border border-slate-200 py-1.5 text-xs text-slate-600 hover:bg-slate-50">
                <UploadCloud className="h-3.5 w-3.5" /> {t(`brandDetail.${key}`)}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => handleLogoUpload(key, e.target.files?.[0])} data-testid={`brand-logo-${key}-input`} />
              </label>
              {brand.logos?.[key] && (
                <button type="button" onClick={() => handleRemoveBg(key)} disabled={removingBg === key} className="mt-1.5 flex w-full items-center justify-center gap-1.5 rounded-md border border-slate-200 py-1.5 text-xs text-slate-500 hover:bg-slate-50 disabled:opacity-60" data-testid={`brand-logo-${key}-removebg`}>
                  <Eraser className="h-3.5 w-3.5" /> {removingBg === key ? t("common.processing") : t("brandDetail.removeWhiteBg")}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 bhf-card p-6">
        <h2 className="font-display text-sm font-700 text-slate-800">{t("brandDetail.fonts")}</h2>
        <div className="mt-4 grid grid-cols-2 gap-4">
          <div><Label>{t("brandDetail.titleFont")}</Label><DebouncedInput key={`title-font-${id}`} className="mt-1" initialValue={brand.fonts?.titleFont || ""} onCommit={(v) => save({ fonts: { ...brand.fonts, titleFont: v } })} data-testid="brand-title-font-input" /></div>
          <div><Label>{t("brandDetail.bodyFont")}</Label><DebouncedInput key={`body-font-${id}`} className="mt-1" initialValue={brand.fonts?.bodyFont || ""} onCommit={(v) => save({ fonts: { ...brand.fonts, bodyFont: v } })} data-testid="brand-body-font-input" /></div>
        </div>
      </div>

      <div className="mt-6 bhf-card p-6">
        <h2 className="font-display text-sm font-700 text-slate-800">{t("brandDetail.colors")}</h2>
        <div className="mt-4 grid grid-cols-3 gap-4">
          {["primary", "secondary", "text"].map((key) => (
            <div key={key}>
              <Label className="capitalize">{key}</Label>
              <div className="mt-1 flex items-center gap-2">
                <input type="color" value={brand.colors?.[key] || "#000000"} onChange={(e) => save({ colors: { ...brand.colors, [key]: e.target.value } })} className="h-9 w-10 rounded border border-slate-200" data-testid={`brand-color-${key}-input`} />
                <DebouncedInput key={`color-text-${key}-${id}`} initialValue={brand.colors?.[key] || ""} onCommit={(v) => save({ colors: { ...brand.colors, [key]: v } })} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 bhf-card p-6">
        <h2 className="font-display text-sm font-700 text-slate-800">{t("brandDetail.backgrounds")}</h2>
        <p className="mt-1 text-xs text-slate-500">{t("brandDetail.bgHint")}</p>
        <div className="mt-4 grid grid-cols-3 gap-4 sm:grid-cols-6">
          {BG_KEYS.map((key) => {
            const val = brand.backgrounds?.[key];
            const isColor = val && val.startsWith("#");
            return (
              <div key={key}>
                <div className="flex h-16 items-center justify-center overflow-hidden rounded-lg border border-slate-200" style={isColor ? { background: val } : {}}>
                  {!isColor && val ? <img src={fileUrl(val)} alt={key} className="h-full w-full object-cover" /> : null}
                </div>
                <div className="mt-1.5 text-[11px] text-slate-500">{t(`brandDetail.${key}`)}</div>
                <input type="color" value={isColor ? val : "#F5F0E8"} onChange={(e) => save({ backgrounds: { ...brand.backgrounds, [key]: e.target.value } })} className="mt-1 h-6 w-full rounded border border-slate-200" data-testid={`brand-bg-${key}-color`} />
                <label className="mt-1 flex cursor-pointer items-center justify-center gap-1 rounded-md border border-slate-200 py-1 text-[10px] text-slate-500 hover:bg-slate-50">
                  <UploadCloud className="h-3 w-3" /> {t("brandDetail.image")}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handleBgUpload(key, e.target.files?.[0])} data-testid={`brand-bg-${key}-input`} />
                </label>
              </div>
            );
          })}
        </div>
      </div>
      </fieldset>
    </div>
  );
}
